/**
 * Listening again in the review (D-106), once the section is over: without limit, with play /
 * pause, back 5 s, speed 0.75× / 1× / 1.25×, and « Show the transcript » (the lines with the
 * characters' names). The generated audio when there is one, else the device's voices read the
 * lines again (one voice per character). Also used for the learner's own EO / Speaking takes,
 * and during a sitting in training mode (D-144: pause and listen again at will; the exam mode keeps
 * the single listening of the test centre).
 */

import { type SpokenLine, fileOfLink } from "@agencyos/core";
import { Pause, Play, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { claimPlayback, speechAvailable } from "./audio";
import { speakLines, stopLines } from "./listening";
import { clock } from "./text";
import "./listening.css";

export const SPEEDS = [0.75, 1, 1.25] as const;

function SpeedPicker({ speed, onChange }: { speed: number; onChange: (value: number) => void }) {
  const { t, i18n } = useTranslation();
  const format = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
  return (
    <div className="ac-replay__speeds" role="group" aria-label={t("listening.replay.speed")}>
      {SPEEDS.map((value) => (
        <button
          key={value}
          type="button"
          className={`ac-replay__speed${speed === value ? " ac-replay__speed--on" : ""}`}
          aria-pressed={speed === value}
          onClick={() => onChange(value)}
        >
          {t("listening.replay.speedValue", { value: format.format(value) })}
        </button>
      ))}
    </div>
  );
}

/**
 * D-181: the link of the same file, kept while the page refreshes its data (every few seconds
 * while the marking runs): a new signature would load the audio again and start it from the
 * beginning. A newer link of the file is used only when the kept one fails (expired).
 */
export function useSteadyLink(url: string): { src: string; renew: () => boolean } {
  const [kept, setKept] = useState(url);
  const src = fileOfLink(kept) === fileOfLink(url) ? kept : url;
  if (src !== kept) setKept(src); // another file: its own link
  const renew = () => {
    if (url === src) return false;
    setKept(url);
    return true;
  };
  return { src, renew };
}

/** A recorded file: the generated audio of a document, or the learner's own recording. */
export function AudioReplay({ url, label, onStart }: { url: string; label: string; onStart?: () => void }) {
  const { t } = useTranslation();
  const link = useSteadyLink(url);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);
  const probing = useRef(false);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed]);
  useEffect(() => () => audio.current?.pause(), []);

  /** A recording made by the browser (webm) has no duration until it is read to the end: the
   * end is asked once, then the player goes back to the start (the slider works, D-114). */
  const measure = (player: HTMLAudioElement) => {
    if (Number.isFinite(player.duration) && player.duration > 0) {
      setDuration(player.duration);
      if (probing.current) {
        probing.current = false;
        player.currentTime = 0;
        setTime(0);
      }
      return;
    }
    if (player.duration === Infinity && !probing.current) {
      probing.current = true;
      player.currentTime = 1e7;
    }
  };

  const toggle = async () => {
    const player = audio.current;
    if (!player) return;
    if (!player.paused) {
      player.pause();
      return;
    }
    try {
      if (player.ended) player.currentTime = 0;
      player.playbackRate = speed;
      claimPlayback(player); // another document or recording stops
      await player.play();
      onStart?.();
    } catch (failure) {
      if (!(failure instanceof DOMException && failure.name === "AbortError")) setFailed(true);
    }
  };

  const back = () => {
    const player = audio.current;
    if (player) player.currentTime = Math.max(0, player.currentTime - 5);
  };

  return (
    <div className="ac-replay" role="group" aria-label={label}>
      {/* The transcript is offered just below (« Show the transcript »), not as a caption track. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audio}
        src={link.src}
        preload="metadata"
        onPlay={(event) => {
          claimPlayback(event.currentTarget);
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(event) => {
          if (!probing.current) setTime(event.currentTarget.currentTime);
        }}
        onLoadedMetadata={(event) => measure(event.currentTarget)}
        onDurationChange={(event) => measure(event.currentTarget)}
        onError={() => setFailed(!link.renew())}
      />
      <div className="ac-replay__row">
        <button type="button" className="ac-replay__main" onClick={() => void toggle()} aria-label={playing ? t("listening.replay.pause") : t("listening.replay.play")}>
          {playing ? <Pause aria-hidden size={20} /> : <Play aria-hidden size={20} />}
        </button>
        <button type="button" className="ac-replay__back" onClick={back}>
          <RotateCcw aria-hidden size={16} />
          {t("listening.replay.back5")}
        </button>
        <input
          className="ac-replay__seek"
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(time, duration || 0)}
          aria-label={t("listening.replay.position")}
          onChange={(event) => {
            if (audio.current) audio.current.currentTime = Number(event.target.value);
          }}
        />
        <span className="ac-replay__time ao-num">
          {clock(Math.floor(time))} / {clock(Math.floor(duration))}
        </span>
      </div>
      <SpeedPicker speed={speed} onChange={setSpeed} />
      {failed && (
        <p className="ac-footnote ko">
          {t("listening.replay.failed")}{" "}
          <a className="link" href={url}>
            {t("listening.replay.download")}
          </a>
        </p>
      )}
    </div>
  );
}

/** No generated audio: the device's voices read the lines again, one voice per character. */
export function SpeechReplay({
  lines,
  lang,
  preferred,
  label,
  onStart,
}: {
  lines: SpokenLine[];
  lang: string;
  preferred: string;
  label: string;
  onStart?: () => void;
}) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => () => stopLines(), []);

  const start = async (from: number) => {
    setPlaying(true);
    onStart?.();
    const result = await speakLines(lines, { lang, preferred, speed, from, onLine: setCurrent });
    if (result !== "stopped") {
      setPlaying(false);
      if (result === "done") setCurrent(0);
    }
  };

  const toggle = () => {
    if (playing) {
      stopLines();
      setPlaying(false);
      return;
    }
    void start(current);
  };

  const back = () => {
    const from = Math.max(0, current - 1);
    setCurrent(from);
    if (playing) void start(from);
  };

  if (!speechAvailable()) return <p className="ac-footnote">{t("listening.replay.noVoice")}</p>;
  return (
    <div className="ac-replay" role="group" aria-label={label}>
      <div className="ac-replay__row">
        <button type="button" className="ac-replay__main" onClick={toggle} aria-label={playing ? t("listening.replay.pause") : t("listening.replay.play")}>
          {playing ? <Pause aria-hidden size={20} /> : <Play aria-hidden size={20} />}
        </button>
        <button type="button" className="ac-replay__back" onClick={back}>
          <RotateCcw aria-hidden size={16} />
          {t("listening.replay.backLine")}
        </button>
        <span className="ac-replay__time ao-num">{t("listening.replay.line", { n: current + 1, total: lines.length })}</span>
      </div>
      <SpeedPicker
        speed={speed}
        onChange={(value) => {
          setSpeed(value);
          if (playing) {
            stopLines();
            setPlaying(false);
          }
        }}
      />
      <p className="ac-footnote">{t("listening.replay.deviceVoices")}</p>
    </div>
  );
}

/** « Show the transcript »: the lines with the characters' names. */
export function Transcript({ lines, lang }: { lines: SpokenLine[]; lang: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!lines.length) return null;
  const nameOf = (line: SpokenLine) =>
    line.speaker || (line.role === "announcer" ? t("listening.transcript.announcer") : t("listening.transcript.narrator"));
  return (
    <div className="ac-transcript">
      <button type="button" className="ac-transcript__toggle link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? t("listening.transcript.hide") : t("listening.transcript.show")}
      </button>
      {open && (
        <dl className="ac-transcript__lines" lang={lang}>
          {lines.map((line, index) => (
            <div key={index} className="ac-transcript__line">
              <dt>{nameOf(line)}</dt>
              <dd>{line.text}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/** A listening document in the review: the audio (or the voices), then its transcript. */
export function ListeningReplay({
  audioUrl,
  lines,
  lang = "fr",
  preferred = "fr-FR",
  context,
}: {
  audioUrl: string | null;
  lines: SpokenLine[];
  lang?: string;
  preferred?: string;
  /** The scene (« [Radio debate…] »): shown small, never read. */
  context?: string;
}) {
  const { t } = useTranslation();
  const label = t("listening.replay.label");
  return (
    <div className="ac-replay-block">
      {context && <p className="ac-replay__context ac-footnote">{context}</p>}
      {audioUrl ? <AudioReplay url={audioUrl} label={label} /> : lines.length > 0 && <SpeechReplay lines={lines} lang={lang} preferred={preferred} label={label} />}
      <Transcript lines={lines} lang={lang} />
    </div>
  );
}

/**
 * A listening document during a sitting in training mode (D-144): the learner presses « Play »,
 * pauses, goes back 5 s (or one line with the device's voices), moves in the audio, changes the
 * speed and listens again as often as they like. `load` asks the link of the generated audio (the
 * server gives it again in training); without audio the device's voices read the lines.
 * `onStart` tells the player the document was heard at least once (the options open).
 */
/** The two notes of the practice player, for the (i) of the audio panel of an exam screen (D-176). */
export function TrainingNote() {
  const { t } = useTranslation();
  return (
    <>
      <p className="ac-footnote">{t("listening.training.start")}</p>
      <p className="ac-footnote">{t("listening.training.note")}</p>
    </>
  );
}

export function TrainingListen({
  load,
  lines,
  lang = "fr",
  preferred = "fr-FR",
  onStart,
}: {
  load: (() => Promise<string>) | null;
  lines: SpokenLine[];
  lang?: string;
  preferred?: string;
  onStart?: () => void;
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const label = t("listening.training.label");

  useEffect(() => {
    if (!load) return;
    let alive = true;
    load()
      .then((value) => alive && setUrl(value))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [load]);

  const withAudio = load !== null && !failed;
  return (
    <div className="ac-replay-block ac-training-listen">
      {withAudio ? (
        url ? (
          <AudioReplay url={url} label={label} onStart={onStart} />
        ) : (
          <p className="ac-footnote" role="status">
            {t("listening.training.loading")}
          </p>
        )
      ) : lines.length > 0 ? (
        <SpeechReplay lines={lines} lang={lang} preferred={preferred} label={label} onStart={onStart} />
      ) : (
        <p className="ko ac-footnote">{t("academy.player.audioProblem")}</p>
      )}
    </div>
  );
}
